import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useRobots } from "@/components/seo/Seo";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight, Check, ChevronDown, Globe, Headset, Link2, Loader2, MapPin,
  MessageSquare, Phone, Plane, Star,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import LiveRouteMap from "@/components/LiveRouteMap";
import { STATUS_ORDER, VEHICLES, IMAGES, formatChf } from "@/lib/data";
import { LOCALES, TRACKING_LANGS, translate, useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || ""; // origine sans /api (assets: photos chauffeurs)

const TRACKING_LANG_NAMES = { fr: "Français", en: "English", de: "Deutsch", ru: "Русский", zh: "中文", ar: "العربية" };

export default function Tracking() {
  useRobots("noindex, nofollow");
  const { code } = useParams();
  const { lang } = useLang();
  // langue de la page de suivi : choix local FR/EN/DE/RU/ZH, indépendant du site
  const [trackLang, setTrackLangState] = useState(() => {
    try {
      const saved = localStorage.getItem("miles-track-lang");
      if (saved && TRACKING_LANGS.includes(saved)) return saved;
    } catch { /* noop */ }
    return TRACKING_LANGS.includes(lang) ? lang : "fr";
  });
  const setTrackLang = (l) => {
    if (!TRACKING_LANGS.includes(l)) return;
    setTrackLangState(l);
    try { localStorage.setItem("miles-track-lang", l); } catch { /* noop */ }
  };
  // t local : puise dans le dictionnaire de la langue choisie (repli FR auto)
  const t = (key) => translate(trackLang, key);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);
  useEffect(() => {
    if (!langMenuOpen) return undefined;
    const close = (e) => { if (langMenuRef.current && !langMenuRef.current.contains(e.target)) setLangMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [langMenuOpen]);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);
  const [routeData, setRouteData] = useState(null);

  const load = useCallback(() => (
    // paramètre anti-cache : Safari iOS garde parfois la réponse GET en cache
    axios.get(`${API}/bookings/${code}`, { params: { _: Date.now() } })
      .then((r) => setBooking(r.data))
      .catch(() => setError(true))
  ), [code]);

  // géométrie OSRM réelle du trajet (une fois par chargement de page)
  useEffect(() => {
    axios.get(`${API}/bookings/${code}/route`)
      .then((r) => setRouteData(r.data))
      .catch(() => setRouteData({ geometry: null }));
  }, [code]);

  useEffect(() => {
    load();
  }, [load]);

  const currentStatus = booking?.status;

  useEffect(() => {
    if (!currentStatus || currentStatus === "completed") return;
    // rafraîchissement rapide dès qu'un chauffeur est attribué (il peut partir d'un instant
    // à l'autre), quasi temps réel pendant la course, plus lent le reste du temps
    const live = ["driver_on_the_way", "driver_arrived", "on_board"].includes(currentStatus);
    const id = setInterval(() => {
      load();
    }, live ? 3000 : currentStatus === "driver_assigned" ? 5000 : 15000);
    return () => clearInterval(id);
  }, [currentStatus, load]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col bg-cloud" data-testid="tracking-not-found">
        <Navbar />
        <div className="flex flex-1 flex-col items-center justify-center px-5 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate">{t("tr.nf_eyebrow")}</p>
          <h1 className="mt-4 text-3xl font-bold text-ink">{t("tr.nf_t")}</h1>
          <p className="mt-3 max-w-sm text-sm text-slate">{t("tr.nf_b")}</p>
          <Link to="/contact" className="mt-8 rounded-full bg-miles px-7 py-3.5 text-sm font-semibold text-white">{t("tr.nf_cta")}</Link>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cloud" data-testid="tracking-loading">
        <Loader2 className="h-6 w-6 animate-spin text-miles" strokeWidth={1.5} />
      </div>
    );
  }

  const idx = STATUS_ORDER.indexOf(booking.status);
  const copy = t(`tr.copy.${booking.status}`);
  const vehicle = VEHICLES[booking.vehicle_category];
  const vehicleModel = vehicle ? t(`veh.${vehicle.id}.model`) : "";
  // heure d'arrivée estimée réelle (maintenant + ETA OSRM) — le client voit l'heure concrète
  const etaClock = booking.live_eta_minutes
    ? new Date(Date.now() + booking.live_eta_minutes * 60000).toLocaleTimeString(LOCALES[trackLang], { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="min-h-screen bg-cloud" data-testid="tracking-page">
      <Navbar />
      <div className="mx-auto max-w-5xl px-5 pb-24 pt-16 sm:px-8 lg:pt-24" dir={trackLang === "ar" ? "rtl" : "ltr"}>
        <div className="mt-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate">{t("tr.eyebrow").replace("{code}", booking.tracking_code)}</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-testid="tracking-status-title">
            <AnimatePresence mode="wait">
              <motion.span key={booking.status} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="block">
                {copy[0]}
              </motion.span>
            </AnimatePresence>
          </h1>
          <p className="mt-2 text-sm text-slate">{copy[1]}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setLangMenuOpen((o) => !o)}
                data-testid="tracking-lang-switch"
                aria-expanded={langMenuOpen}
                className="inline-flex items-center gap-2 rounded-full bg-miles px-4 py-2.5 text-xs font-semibold text-white shadow-[0_6px_20px_-6px_rgba(36,87,255,0.6)] ring-2 ring-miles/20 transition-colors duration-300 hover:bg-miles-deep"
              >
                <Globe className="h-4 w-4" strokeWidth={2} />
                {TRACKING_LANG_NAMES[trackLang]}
                <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${langMenuOpen ? "rotate-180" : ""}`} strokeWidth={2} />
              </button>
              {langMenuOpen && (
                <div className="absolute left-0 top-full z-[1100] mt-2 w-44 overflow-hidden rounded-2xl border border-ink/10 bg-white p-1.5 shadow-xl" data-testid="tracking-lang-menu">
                  {TRACKING_LANGS.map((l) => (
                    <button
                      key={l}
                      data-testid={`tracking-lang-${l}`}
                      onClick={() => { setTrackLang(l); setLangMenuOpen(false); }}
                      className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold transition-colors duration-200 ${trackLang === l ? "bg-miles-ice text-miles" : "text-ink hover:bg-cloud"}`}
                    >
                      {TRACKING_LANG_NAMES[l]}
                      {trackLang === l && <Check className="h-3.5 w-3.5" strokeWidth={2.5} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(window.location.href);
                toast.success(t("tr.share_t"), { description: t("tr.share_d") });
              }}
              data-testid="tracking-share-button"
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2.5 text-xs font-semibold text-ink transition-colors duration-300 hover:border-miles hover:text-miles"
            >
              <Link2 className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("tr.share")}
            </button>
          </div>
        </div>

        <div className="mt-8 grid gap-5 lg:grid-cols-5">
          <div className="space-y-5 lg:col-span-3">
            <div className="overflow-hidden rounded-3xl border border-ink/10 bg-white shadow-sm" data-testid="tracking-map-card">
              <LiveRouteMap
                geometry={routeData?.geometry}
                center={routeData?.center}
                driverLocation={booking.driver_location}
                driverRoute={booking.driver_route}
                status={booking.status}
                etaMinutes={booking.live_eta_minutes}
                driverLocationAge={booking.driver_location_age_s}
                staleLabel={t("tr.pos_stale")}
                fromLabel={booking.pickup}
                toLabel={booking.dropoff || t("tr.hourly_dest")}
                height="h-80 sm:h-96"
                testId="tracking-map"
              />
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink/5 p-5">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">
                    {booking.status === "on_board" ? t("tr.arrival_label") : booking.status === "completed" ? t("tr.completed_label") : t("tr.pickup_label")}
                  </p>
                  <p className="mt-1 text-2xl font-bold tracking-tight text-ink" data-testid="tracking-eta">{etaClock || booking.time}</p>
                </div>
              </div>
              {booking.stops?.length > 0 && (
                <div className="border-t border-ink/5 px-5 pb-5" data-testid="tracking-stops">
                  <p className="mb-2 mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate">{t("tr.stops_label")}</p>
                  <div className="space-y-1.5">
                    {booking.stops.map((s, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-sm" data-testid={`tracking-stop-${i}`}>
                        <span className="min-w-0 flex-1 truncate text-ink"><span className="text-slate">{i + 1}.</span> {s.address}</span>
                        {s.wait_minutes > 0 && <span className="shrink-0 font-mono text-xs text-slate">{s.wait_minutes} min</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {booking.driver && booking.status !== "cancelled" ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-ink/10 bg-white p-6 shadow-sm"
                data-testid="driver-card"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">{t("tr.your_driver")}</p>
                <div className="mt-4 flex flex-wrap items-center gap-5">
                  <img src={booking.driver.photo_url ? `${BACKEND}${booking.driver.photo_url}` : IMAGES.driverPortrait} alt={t("tr.driver_alt").replace("{name}", booking.driver.first_name)}
                    className="h-16 w-16 rounded-2xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-lg font-bold text-ink">
                      {booking.driver.first_name} {booking.driver.last_initial}
                      <span className="flex items-center gap-1 text-sm font-semibold text-slate">
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" strokeWidth={1.5} /> {booking.driver.rating}
                      </span>
                    </p>
                    <p className="mt-1 text-sm text-slate">{vehicleModel || booking.driver.vehicle_model} · {booking.driver.vehicle_color}</p>
                    <p className="mt-1 font-mono text-xs font-semibold text-ink">{booking.driver.plate}</p>
                  </div>
                  <div className="flex gap-2">
                    {booking.driver.phone && (() => {
                      const raw = booking.driver.phone;
                      const tel = raw.replace(/[^\d+]/g, "");
                      let wa = raw.replace(/[^\d]/g, "");
                      if (raw.trim().startsWith("+")) wa = wa;
                      else if (wa.startsWith("00")) wa = wa.slice(2);
                      else if (wa.startsWith("0")) wa = "41" + wa.slice(1);
                      return (
                        <>
                          <a href={`tel:${tel}`} data-testid="driver-call-button"
                            className="flex h-11 w-11 items-center justify-center rounded-full bg-miles text-white transition-transform hover:-translate-y-0.5" aria-label={t("tr.call_driver")}>
                            <Phone className="h-4 w-4" strokeWidth={1.5} />
                          </a>
                          <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" data-testid="driver-message-button"
                            className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] text-white transition-transform hover:-translate-y-0.5" aria-label="WhatsApp">
                            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                          </a>
                        </>
                      );
                    })()}
                  </div>
                </div>
                {booking.status === "driver_arrived" && (
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="mt-5 flex items-start gap-2 rounded-2xl bg-miles-ice px-4 py-3 text-sm text-miles-dark" data-testid="meeting-point-note">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
                    {t("tr.meeting").replace("{pickup}", booking.pickup).replace("{terminal}", booking.terminal ? `, ${booking.terminal}` : "")}
                  </motion.p>
                )}
              </motion.div>
            ) : booking.status === "cancelled" ? null : (
              <div className="rounded-3xl border border-dashed border-ink/15 bg-white/60 p-6 text-sm text-slate" data-testid="driver-pending-card">
                {t("tr.pending")}
              </div>
            )}

            {booking.status === "completed" && booking.price_chf != null && (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-ink/10 bg-white p-6 shadow-sm" data-testid="receipt-card">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">{t("tr.receipt").replace("{code}", booking.tracking_code)}</p>
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold text-emerald-600">{t("tr.paid_badge")}</span>
                </div>
                <div className="mt-4 space-y-2.5 border-t border-dashed border-ink/10 pt-4 text-sm">
                  <div className="flex justify-between"><span className="text-slate">{booking.pickup} → {booking.dropoff || t("tr.hourly_dest")}</span><span className="font-semibold">{formatChf(booking.price_chf)}</span></div>
                  <div className="flex justify-between"><span className="text-slate">{t("tr.waiting_line")}</span><span className="font-semibold">{t("tr.included")}</span></div>
                  <div className="flex justify-between border-t border-dashed border-ink/10 pt-2.5"><span className="font-semibold text-ink">{t("tr.total")}</span><span className="font-bold text-ink">{formatChf(booking.price_chf)}</span></div>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link to="/reservation" data-testid="book-again-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-miles px-5 py-2.5 text-xs font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-miles-dark">
                    {t("tr.rebook")} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </Link>
                </div>
              </motion.div>
            )}
          </div>

          <div className="space-y-5 lg:col-span-2">
            <div className={`rounded-3xl border border-ink/10 bg-white p-6 shadow-sm ${booking.status === "cancelled" ? "opacity-40" : ""}`} data-testid="tracking-timeline">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">{t("tr.timeline_t")}</p>
              <div className="mt-5 space-y-0">
                {STATUS_ORDER.map((s, i) => {
                  const done = i < idx;
                  const current = i === idx;
                  const hist = (booking.status_history || []).find((h) => h.status === s);
                  return (
                    <div key={s} className="relative flex gap-3.5 pb-6 last:pb-0" data-testid={`timeline-step-${s}`}>
                      {i < STATUS_ORDER.length - 1 && (
                        <span className={`absolute left-[9px] top-6 h-full w-0.5 rounded-full ${done ? "bg-miles" : "bg-ink/10"}`} />
                      )}
                      <span className={`relative flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-500 ${
                        done ? "border-miles bg-miles" : current ? "border-miles bg-white" : "border-ink/15 bg-white"
                      }`}>
                        {done ? <Check className="h-2.5 w-2.5 text-white" strokeWidth={3.5} />
                          : current ? <span className="relative flex h-1.5 w-1.5"><span className="ping-soft absolute inline-flex h-full w-full rounded-full bg-miles" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-miles" /></span>
                          : null}
                      </span>
                      <div className={!done && !current ? "opacity-40" : ""}>
                        <p className="text-sm font-semibold text-ink">{t(`statuses.${s}`)}</p>
                        {hist && (
                          <p className="mt-0.5 font-mono text-[10px] text-slate">
                            {new Date(hist.at).toLocaleTimeString(LOCALES[trackLang], { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {booking.flight_number && (
              <div className="rounded-3xl border border-miles/20 bg-miles-ice/70 p-6" data-testid="flight-tracking-card">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-miles">
                  <Plane className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("tr.flight_t")}
                </p>
                <p className="mt-2 text-lg font-bold text-ink">{booking.flight_number}</p>
                <p className="mt-1 text-xs leading-relaxed text-slate">
                  {t("tr.flight_note")}
                </p>
              </div>
            )}

            <Link to="/contact" data-testid="tracking-support-link"
              className="flex items-center gap-3 rounded-3xl border border-ink/10 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-miles-ice text-miles">
                <Headset className="h-4.5 w-4.5 h-[18px] w-[18px]" strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-sm font-semibold text-ink">{t("tr.support_t")}</p>
                <p className="text-xs text-slate">{t("tr.support_b")}</p>
              </div>
            </Link>
          </div>
        </div>

        {booking.booked_for_you_by && (
          <p className="mt-12 text-center text-xs text-slate" data-testid="booked-for-you-by">
            {t("tr.booked_by")} {booking.booked_for_you_by}
          </p>
        )}
      </div>
    </div>
  );
}
